import { getMarqueeCardImageUrl } from '#src/app/pages/homepage/shared/utils';

import type {
	CloseToGoalCardData,
	I18n,
	MarqueeCardBase,
	MarqueeItem,
	MilestoneCardData,
	MomentumCardData,
	PetitionCardData,
	QuoteCardData,
	QuoteCardType,
	SimplePetitionCardData,
} from './types';

/** FCM card types that render as a quote, mapped to the card data's own discriminant. */
const QUOTE_CARD_TYPES: Partial<Record<MarqueeItem['cardType'], QuoteCardType>> = {
	STARTER_UPDATE: 'starterUpdate',
	DECISION_MAKER: 'decisionMaker',
	ENDORSEMENT: 'endorsement',
};

export function buildSupporterVoiceCard(
	item: MarqueeItem,
	base: MarqueeCardBase,
	{ translate }: I18n,
): QuoteCardData | undefined {
	const { featuredComment, summarizedTitle } = item;
	if (!featuredComment) return undefined;

	return {
		...base,
		type: 'supporterVoice',
		relativeTimeLabel: featuredComment.createdAtRelativeLocalized,
		label: translate('corgi.homepage.petition_marquee.supporter_voice_label', {
			name: featuredComment.user.displayName,
		}),
		quote: featuredComment.comment,
		subLabel: translate('corgi.homepage.petition_marquee.on_petition', { title: summarizedTitle }),
	};
}

/**
 * Each kind keeps its attribution and its body somewhere different, and endorsements carry
 * no user at all. The concrete update kind - not the entry's cardType - is what proves which
 * fields exist: a pinned update of the wrong kind must drop the card, not mis-attribute it.
 */
function buildUpdateQuote(
	quoteType: QuoteCardType,
	featuredUpdate: NonNullable<MarqueeItem['featuredUpdate']>,
	{ translate }: I18n,
): { label: string; quote: string } | undefined {
	if (quoteType === 'starterUpdate' && featuredUpdate.__typename === 'StarterPetitionUpdate') {
		return {
			label: translate('corgi.homepage.petition_marquee.starter_update_label', {
				name: featuredUpdate.user.displayName,
			}),
			quote: featuredUpdate.descriptionStripped,
		};
	}
	// a DM response's `user` is the petition starter, not the decision maker -
	// petition-service puts the decision maker's name in the update title
	if (quoteType === 'decisionMaker' && featuredUpdate.__typename === 'DmResponsePetitionUpdate') {
		return {
			label: translate('corgi.homepage.petition_marquee.decision_maker_label', {
				name: featuredUpdate.title,
			}),
			quote: featuredUpdate.descriptionStripped,
		};
	}
	// an endorsement's text is `content`; its descriptionStripped is always empty
	if (quoteType === 'endorsement' && featuredUpdate.__typename === 'EndorsementPetitionUpdate') {
		return {
			label: translate('corgi.homepage.petition_marquee.endorsement_label', {
				name: featuredUpdate.title,
			}),
			quote: featuredUpdate.content ?? '',
		};
	}
	return undefined;
}

export function buildUpdateQuoteCard(item: MarqueeItem, base: MarqueeCardBase, i18n: I18n): QuoteCardData | undefined {
	const { cardType, featuredUpdate, summarizedTitle } = item;
	const quoteType = QUOTE_CARD_TYPES[cardType];
	if (!quoteType || !featuredUpdate) return undefined;

	const quote = buildUpdateQuote(quoteType, featuredUpdate, i18n);
	// an update whose body resolved empty would render a blank quote - drop it instead
	if (!quote?.quote) return undefined;

	return {
		...base,
		type: quoteType,
		relativeTimeLabel: featuredUpdate.createdAtRelativeLocalized,
		subLabel: i18n.translate('corgi.homepage.petition_marquee.on_petition', { title: summarizedTitle }),
		...quote,
	};
}

export function buildMilestoneCard(
	item: MarqueeItem,
	base: MarqueeCardBase,
	{ translatePlural }: I18n,
): MilestoneCardData | undefined {
	const { featuredUpdate } = item;
	// milestone updates are the only kind carrying the signature count they mark
	if (featuredUpdate?.__typename !== 'MilestonePetitionUpdate') return undefined;

	// an entry with no usable image is dropped rather than shown with a placeholder
	const imageUrl = getMarqueeCardImageUrl(item);
	if (!imageUrl) return undefined;

	return {
		...base,
		type: 'milestone',
		imageUrl,
		relativeTimeLabel: featuredUpdate.createdAtRelativeLocalized,
		milestoneSignaturesLabel: translatePlural(
			'corgi.components.petition_card.signatures',
			featuredUpdate.signatureCount,
			{ countLocalized: featuredUpdate.signatureCountLocalized },
		),
	};
}

export function buildCloseToGoalCard(
	item: MarqueeItem,
	base: MarqueeCardBase,
	{ translate }: I18n,
): CloseToGoalCardData | undefined {
	const { signatureCount, signatureGoal } = item.petition.signatureState;
	if (signatureGoal.displayed <= 0) return undefined;

	const percent = Math.min(100, Math.round((signatureCount.displayed / signatureGoal.displayed) * 100));

	return {
		...base,
		type: 'closeToGoal',
		goalPercent: percent,
		// the sign sits in the translation so each locale can place it, which keeps the
		// formatting out of render - Intl here would run under SSR and trip the i18n guard
		goalPercentLabel: translate('corgi.homepage.petition_marquee.close_to_goal_percent', {
			percent: String(percent),
		}),
		goalSignaturesLabel: signatureGoal.displayedLocalized,
	};
}

export function buildMomentumCard(
	{ petition }: MarqueeItem,
	base: MarqueeCardBase,
	{ translatePlural }: I18n,
): MomentumCardData {
	return {
		...base,
		type: 'momentum',
		dailySignaturesLabel: translatePlural(
			'corgi.homepage.petition_marquee.momentum_count',
			petition.dailySignatureCount,
			{ countLocalized: petition.dailySignatureCountLocalized },
		),
	};
}

export function buildPetitionCard(
	item: MarqueeItem,
	base: MarqueeCardBase,
	{ translatePlural }: I18n,
): PetitionCardData | undefined {
	const { petition } = item;
	// a curator can set PETITION on a petition that hasn't won - only announce a
	// victory that has actually happened, and show the rest as trending
	const isVictory = petition.status === 'VICTORY';
	const { displayed, displayedLocalized } = petition.signatureState.signatureCount;

	// an entry with no usable image is dropped rather than shown with a placeholder
	const imageUrl = getMarqueeCardImageUrl(item);
	if (!imageUrl) return undefined;

	return {
		...base,
		type: 'petition',
		isVictory,
		imageUrl,
		relativeTimeLabel: isVictory ? (petition.victoryDateRelativeLocalized ?? undefined) : undefined,
		signaturesLabel: translatePlural('corgi.components.petition_card.signatures', displayed, {
			countLocalized: displayedLocalized,
		}),
	};
}

export function buildSimplePetitionCard(
	item: MarqueeItem,
	base: MarqueeCardBase,
	i18n: I18n,
): SimplePetitionCardData | undefined {
	const card = buildPetitionCard(item, base, i18n);

	// the relative time lives in the header row, which this card doesn't render
	return card && { ...card, type: 'simplePetition', relativeTimeLabel: undefined };
}
